import React from 'react';
import { motion, useReducedMotion, Variants } from 'framer-motion';

interface SplitTextProps {
  text: string;
  delay?: number;
  staggerChildren?: number;
  className?: string;
  emphasize?: boolean;
}

const container = (delay: number, stagger: number): Variants => ({
  hidden: {},
  visible: {
    transition: {
      delayChildren: delay,
      staggerChildren: stagger,
    },
  },
});

const child: Variants = {
  hidden: { opacity: 0, y: '0.6em', filter: 'blur(8px)' },
  visible: {
    opacity: 1,
    y: 0,
    filter: 'blur(0px)',
    transition: { duration: 0.6, ease: [0.2, 0.7, 0.2, 1] },
  },
};

export const SplitText: React.FC<SplitTextProps> = ({
  text,
  delay = 0,
  staggerChildren = 0.045,
  className,
  emphasize,
}) => {
  const reduce = useReducedMotion();

  if (reduce) {
    return emphasize ? <em className={className}>{text}</em> : <span className={className}>{text}</span>;
  }

  const Wrapper: any = emphasize ? motion.em : motion.span;
  const letters = Array.from(text);

  return (
    <Wrapper
      className={className}
      variants={container(delay, staggerChildren)}
      initial="hidden"
      animate="visible"
      aria-label={text}
      style={{ display: 'inline-block', fontStyle: emphasize ? 'normal' : undefined }}
    >
      {letters.map((ch, i) => (
        <motion.span
          key={`${ch}-${i}`}
          variants={child}
          style={{ display: 'inline-block', whiteSpace: ch === ' ' ? 'pre' : undefined }}
          aria-hidden="true"
        >
          {ch}
        </motion.span>
      ))}
    </Wrapper>
  );
};
